import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMessage } from "@/lib/api";
import { PageTitle, Loading, ErrorBox, Empty, Section } from "@/components/common";

const Members = ({ team, onSaved }) => {
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(new Set(team.member_ids));
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/teams/${team.id}/candidates`).then((r) => setUsers(r.data)).catch(() => {}); }, [team.id]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/teams/${team.id}/members`, { member_ids: [...sel] });
      toast.success("Membres de l'équipe enregistrés.");
      onSaved();
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };
  return (
    <details className="mt-4 rounded-lg border border-border p-3" data-testid={`team-${team.id}-members-editor`}>
      <summary className="cursor-pointer text-sm font-semibold">Gérer les membres de l'équipe</summary>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {users.filter((u) => u.id !== team.leader_id).map((u) => (
          <label key={u.id} className="flex min-h-[44px] items-center gap-3 rounded-md border border-border px-3 text-sm">
            <input type="checkbox" data-testid={`member-${team.id}-${u.id}`} className="h-5 w-5 accent-amber-500" checked={sel.has(u.id)}
              onChange={() => setSel((s) => { const n = new Set(s); n.has(u.id) ? n.delete(u.id) : n.add(u.id); return n; })} /> {u.name}
          </label>
        ))}
      </div>
      <Button className="mt-3" data-testid={`members-save-${team.id}`} onClick={save} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enregistrer les membres</Button>
    </details>
  );
};

const Activity = ({ team }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/teams/${team.id}/activity`).then((r) => setData(r.data)).catch(() => setData({ take5s: [], inspections: [] })); }, [team.id]);
  if (!data) return null;
  return (
    <details className="mt-3 rounded-lg border border-border p-3" data-testid={`team-${team.id}-activity`}>
      <summary className="cursor-pointer text-sm font-semibold">Activité de l'équipe ({data.take5s.length} Take 5 · {data.inspections.length} inspection(s))</summary>
      <div className="mt-2 space-y-1 text-sm">
        {data.take5s.map((t) => <Link key={t.id} to={`/take5/${t.id}`} className="block rounded px-2 py-1 hover:bg-white/5">Take 5 · {t.task || "(sans titre)"} — {t.user_name} · {t.status}</Link>)}
        {data.inspections.map((i) => <p key={i.id} className="px-2 py-1">Inspection · {i.equipment_code || ""} — {i.user_name} · {i.result || i.status}</p>)}
        {!data.take5s.length && !data.inspections.length && <p className="text-muted-foreground">Aucune activité collective pour l'instant.</p>}
      </div>
    </details>
  );
};

export default function Teams() {
  const [teams, setTeams] = useState(null);
  const [error, setError] = useState("");
  const load = () => api.get("/teams/mine").then((r) => setTeams(r.data)).catch((e) => setError(errMessage(e)));
  useEffect(() => { load(); }, []);
  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!teams) return <Loading />;
  return (
    <div className="space-y-5">
      <PageTitle kicker="Workplace" title="Mes équipes" testId="teams-title" />
      {!teams.length && <Empty testId="teams-empty">Vous n'appartenez à aucune équipe. L'administrateur les crée dans Administration &gt; Équipes.</Empty>}
      {teams.map((t) => (
        <Section key={t.id} title={t.name} icon={Users} testId={`team-${t.id}`}>
          <p className="text-sm text-muted-foreground">Chef : {t.leader_name} · {t.members.length} membre(s) permanent(s)</p>
          <ul data-testid={`team-${t.id}-members`} className="mt-3 flex flex-wrap gap-2">
            {t.members.map((m) => (
              <li key={m.id} className="rounded-full border border-border px-3 py-1 text-sm">{m.name}{m.id === t.leader_id && <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">Chef</span>}</li>
            ))}
          </ul>
          {t.is_leader && <Members team={t} onSaved={load} />}
          {t.is_leader && <Activity team={t} />}
        </Section>
      ))}
    </div>
  );
}
